import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Search, X as XIcon, Package, Box } from "lucide-react";

/**
 * Searchable combobox pro výběr ingredience receptu — surovina nebo polotovar (výrobek).
 *
 * Props:
 *  - materials: array of { id, name, unit }
 *  - products:  array of { id, name } (předfiltrované – jen nevymazané, bez aktuálního výrobku)
 *  - value:     { kind: "material"|"product", id: string } | null
 *  - onChange:  (next: { kind, id } | null) => void
 *  - testid:    base test-id (default "ing-combobox")
 */
export default function IngredientCombobox({ materials = [], products = [], value, onChange, testid = "ing-combobox" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("material"); // "material" | "product"
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => {
    if (!value) return null;
    if (value.kind === "material") {
      const m = materials.find((x) => x.id === value.id);
      return m ? { kind: "material", ...m } : null;
    }
    if (value.kind === "product") {
      const p = products.find((x) => x.id === value.id);
      return p ? { kind: "product", ...p } : null;
    }
    return null;
  }, [value, materials, products]);

  useEffect(() => {
    if (selected?.kind) setTab(selected.kind);
  }, [selected]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    const list = tab === "material" ? materials : products;
    if (!q) return list;
    return list.filter((x) => x.name.toLowerCase().includes(q));
  }, [q, tab, materials, products]);

  const handleSelect = (item) => {
    onChange({ kind: tab, id: item.id });
    setQuery("");
    setOpen(false);
  };

  const displayLabel = () => {
    if (!selected) return "Vyberte surovinu nebo polotovar…";
    if (selected.kind === "material") {
      return `${selected.name} (${selected.unit})`;
    }
    return `🧱 ${selected.name} (polotovar)`;
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid={`${testid}-trigger`}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setTimeout(() => inputRef.current?.focus(), 30);
        }}
        className="input-cafe w-full text-left flex items-center justify-between gap-2"
      >
        <span className={`truncate ${selected ? "" : "text-[#9CA39C]"}`}>{displayLabel()}</span>
        <ChevronsUpDown className="w-4 h-4 text-[#57534E] shrink-0" />
      </button>
      {open && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute z-50 mt-1 w-full min-w-[280px] bg-white border border-[#E5E1D8] rounded-lg shadow-lg overflow-hidden"
        >
          {/* Tabs */}
          <div className="flex border-b border-[#E5E1D8] text-xs">
            <button
              data-testid={`${testid}-tab-material`}
              type="button"
              onClick={() => setTab("material")}
              className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${
                tab === "material" ? "bg-[#F9F6F0] text-[#3A2D27] font-medium" : "text-[#57534E] hover:bg-[#FBFAF7]"
              }`}
            >
              <Package className="w-3.5 h-3.5" /> Suroviny ({materials.length})
            </button>
            <button
              data-testid={`${testid}-tab-product`}
              type="button"
              onClick={() => setTab("product")}
              className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${
                tab === "product" ? "bg-[#F9F6F0] text-[#3A2D27] font-medium" : "text-[#57534E] hover:bg-[#FBFAF7]"
              }`}
            >
              <Box className="w-3.5 h-3.5" /> Polotovary ({products.length})
            </button>
          </div>
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E1D8]">
            <Search className="w-4 h-4 text-[#57534E]" />
            <input
              ref={inputRef}
              data-testid={`${testid}-search`}
              type="text"
              placeholder={tab === "material" ? "Hledat surovinu…" : "Hledat polotovar…"}
              className="flex-1 outline-none text-sm bg-transparent"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length > 0) {
                  e.preventDefault();
                  handleSelect(filtered[0]);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="p-1 rounded hover:bg-[#F9F6F0]">
                <XIcon className="w-3.5 h-3.5 text-[#57534E]" />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-sm text-[#57534E] text-center">
                {tab === "material" ? "Žádná surovina" : "Žádný polotovar"}
              </li>
            )}
            {filtered.map((it) => {
              const isSel = selected?.kind === tab && selected?.id === it.id;
              return (
                <li key={it.id}>
                  <button
                    data-testid={`${testid}-opt-${tab}-${it.id}`}
                    type="button"
                    onClick={() => handleSelect(it)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-[#F9F6F0] ${
                      isSel ? "bg-[#F9F6F0]" : ""
                    }`}
                  >
                    <span className="truncate">{it.name}</span>
                    <span className="text-xs text-[#57534E] shrink-0">
                      {tab === "material" ? it.unit : "polotovar"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
